<template>
  <table class="detail-table w-100">
    <thead>
      <tr v-if="isHorizontal">
        <th>Day</th>
        <th>Date</th>
        <th>Shift</th>
        <th>Shift Allowance</th>
        <th>Hours worked</th>
        <th>Overtime</th>
        <th v-if="Can(rights.HOURS_EXTRA_PAYMENT)">Extra Payment</th>
        <th>Overurentoeslag</th>
        <th>Status</th>
      </tr>
      <tr v-else>
        <th></th>
        <th v-for="rec in details" :key="rec.date">
          {{ weekDay(rec.date) }}
        </th>
      </tr>
    </thead>

    <tbody v-if="isHorizontal">
      <tr v-for="rec in details" :key="rec.date" :data-hour-id="rec.id" :class="{ 'text-muted': rec.outsidePlacement }">
        <td>{{ weekDay(rec.date) }}</td>
        <td>
          {{ rec.date }}
          <span v-if="rec.outsidePlacement" class="badge badge-secondary ml-1 outside-placement">{{ outsidePlacementLabel }}</span>
        </td>

        <td>
          <select class="form-control"
            v-model="rec.shift"
            :disabled="readOnly || acceptsNoNewHours(rec)"
            @change="onShiftChange(rec)"
          >
            <option disabled :value="null">— select shift —</option>
            <option v-for="opt in shiftsForDay(rec.date)"
                    :key="opt.id" :value="opt">
              {{ cap(opt.name) }}
            </option>
          </select>
        </td>

        <td>
          <input class="form-control" :value="rec.shift_allowance"
            :disabled="readOnly || !rec.shift"
            @input="numberInput(rec,'shift_allowance',$event,1000)"
            @blur="cleanTrailingDot(rec,'shift_allowance')"
          />
        </td>

        <td>
          <input class="form-control" :value="rec.hours"
            :disabled="readOnly || !rec.shift"
            @input="numberInput(rec,'hours',$event,12)"
            @blur="cleanTrailingDot(rec,'hours')"
          />
        </td>

        <td>
          <input class="form-control" :value="rec.overtime"
            :disabled="readOnly || !rec.shift"
            @input="numberInput(rec,'overtime',$event,12)"
            @blur="cleanTrailingDot(rec,'overtime')"
          />
        </td>

        <td v-if="Can(rights.HOURS_EXTRA_PAYMENT)">
          <input class="form-control" :value="rec.extra_payment"
          :disabled="readOnly || !rec.shift || !rec.shift.allow_reimbursement"
          @input="numberInput(rec,'extra_payment',$event,1000)"
          @blur="cleanTrailingDot(rec,'extra_payment')"
          />
        </td>

        <td>{{ rec.shift ? rec.shift.overtime_percentage : '—' }}</td>

        <td data-type="status">
          <select class="form-control"
            v-model="rec.status"
            v-bind="$attrs"
            :disabled="readOnly || ('disabled' in $attrs) || !rec.shift || (rec.status === 'approved' && rec.approved_by !== null)"
            @input="emitUpdate(rec)"
          >
            <option v-for="opt in statusOptions" :key="opt" :value="opt">
              {{ cap(opt) }}
            </option>
          </select>
        </td>
      </tr>
    </tbody>
    <tbody v-else>
      <tr>
        <th>Date</th>
        <td v-for="rec in details" :key="'date-'+rec.date">
          {{ rec.date }}
          <span v-if="rec.outsidePlacement" class="badge badge-secondary d-block mt-1 outside-placement">{{ outsidePlacementLabel }}</span>
        </td>
      </tr>

      <tr>
        <th>Shift</th>
        <td v-for="rec in details" :key="'sh-'+rec.date">
          <select class="form-control"
                  v-model="rec.shift"
                  :disabled="readOnly || acceptsNoNewHours(rec)"
                  @change="onShiftChange(rec)"
          >
            <option disabled :value="null">— select shift —</option>
            <option v-for="opt in shiftsForDay(rec.date)"
                    :key="opt.id" :value="opt">
              {{ cap(opt.name) }}
            </option>
          </select>
        </td>
      </tr>

      <tr>
        <th>Hours worked</th>
        <td v-for="rec in details" :key="'hw-'+rec.date">
          <input class="form-control" :value="rec.hours"
                  :disabled="readOnly || !rec.shift"
                  @input="numberInput(rec,'hours',$event,12)"
                  @blur="cleanTrailingDot(rec,'hours')"
          />
        </td>
      </tr>

      <tr>
        <th>Overtime</th>
        <td v-for="rec in details" :key="'ot-'+rec.date">
          <input class="form-control" :value="rec.overtime"
                  :disabled="readOnly || !rec.shift"
                  @input="numberInput(rec,'overtime',$event,12)"
                  @blur="cleanTrailingDot(rec,'overtime')"
          />
        </td>
      </tr>

      <tr v-if="Can(rights.HOURS_EXTRA_PAYMENT)">
        <th>Extra Payment</th>
        <td v-for="rec in details" :key="'ep-'+rec.date">
          <input class="form-control" :value="rec.extra_payment"
                  :disabled="readOnly || !rec.shift || !rec.shift.allow_reimbursement"
                  @input="numberInput(rec,'extra_payment',$event,1000)"
                  @blur="cleanTrailingDot(rec,'extra_payment')"
                  />
        </td>
      </tr>

      <tr>
        <th>Overurentoeslag</th>
        <td v-for="rec in details" :key="'ot-pct-'+rec.date">
          {{ rec.shift ? rec.shift.overtime_percentage : '—' }}
        </td>
      </tr>

      <tr>
        <th>Status</th>
        <td v-for="rec in details" :key="'st-'+rec.date">
          <select class="form-control"
                  v-model="rec.status"
                  :disabled="readOnly || !rec.shift"
                  @input="emitUpdate(rec)">
            <option v-for="opt in statusOptions" :key="opt" :value="opt">
              {{ cap(opt) }}
            </option>
          </select>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
  import DateTools from '@/plugins/dateTools';
  import { RIGHTS } from '../../views/roles/rights';
  import { Can } from '../../../permission/ability.js';

  export default {
    name: 'HoursDetail',
    props: {
      data: { 
        type:Object, 
        required:true 
      },
      shifts: { 
        type:Array , 
        default:() => [] 
      },
      isHorizontal: { 
        type:Boolean, 
        required:true 
      },
      readOnly: {
        type: Boolean,
        default: false
      }
    },
    data () {   
      return {
        statusOptions: ['pending', 'approved'],
        outsidePlacementLabel: 'Outside placement',
        rights: {}
      };
    },
    computed: {
      details () {
        return this.data._hoursDetails
      }
    },
    async created() {
        this.rights =RIGHTS;
    },
    methods: {
      Can,
      cap(s){ 
        return s.charAt(0).toUpperCase() + s.slice(1) 
      },
      weekDay(d){ 
        return DateTools.getWeekdayName(d) 
      },
      acceptsNoNewHours(rec) {
        return rec.outsidePlacement && rec.id === null;
      },
      shiftsForDay(isoDate) {
        const dow = ['mon','tue','wed','thu','fri','sat','sun'][new Date(isoDate+'T00:00').getUTCDay()];
        return this.shifts.filter(s => s.days ? s.days[dow] : false);
      },
      numberInput(rec, field, ev, max) {
        let raw = ev.target.value.replace(/[^0-9.]/g, '');
        raw = raw.replace(/^([^.]*\.)|\./g, '$1');
        if (raw.includes('.')) {
          let [intPart, decPart] = raw.split('.');
          intPart = intPart.replace(/^0+(?=\d)/, '');
          raw = intPart + '.' + (decPart ? decPart.slice(0, 2) : '');
        } else {
          raw = raw.replace(/^0+(?=\d)/, '');
        }
        let num = Number(raw);
        if (!isNaN(num) && max !== undefined && num > max) {
          raw = String(max);
          num = max;
        }
        rec[field] = raw;
        ev.target.value = raw;
        this.emitUpdate(rec);
      },
      cleanTrailingDot(rec, field) {
        if (typeof rec[field] === 'string' && rec[field].endsWith('.')) {
          rec[field] = rec[field].slice(0, -1);
        }
      },
      emitUpdate (rec) {
        this.$emit('detail-updated', this.details, rec);
      },
      onShiftChange (rec) {
        if (!rec.shift || !rec.shift.allow_reimbursement) {
          rec.extra_payment = 0;
        }
        rec.shift_allowance = rec.shift ? rec.shift.shift_allowance_percentage : 0;
        this.emitUpdate(rec);
      }
    }
  };
</script>

<style scoped>
  .detail-table th,
  .detail-table td {
    padding: 1rem;
    vertical-align: middle;
  }
  .form-control {
    font-size: 1.2rem;
    height: auto;
    padding: 0.25rem 0.5rem;
  }
</style>
